import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import type { StaticLegislationBriefingLaw } from '../../utils/staticLegislationBriefing'
import { StaticLegislationComingSoonBadge } from './StaticLegislationComingSoonBadge'

const COMING_SOON_BASE =
  'v2.pages.products.detail.staticLegislationBriefing.comingSoon'
const LAWS_WITH_OVERVIEW = new Set<StaticLegislationBriefingLaw>([
  'weee',
  'fgas',
  'ped',
])
const FAKE_STEP_BARS = [
  { title: 'w-40', body: 'w-3/4', done: true, action: false },
  { title: 'w-52', body: 'w-2/3', done: false, action: true },
  { title: 'w-36', body: 'w-5/6', done: false, action: true },
  { title: 'w-48', body: 'w-1/2', done: false, action: true },
  { title: 'w-44', body: 'w-3/5', done: false, action: false },
  { title: 'w-56', body: 'w-2/3', done: false, action: true },
  { title: 'w-32', body: 'w-4/5', done: false, action: true },
  { title: 'w-40', body: 'w-1/2', done: false, action: true },
] as const

type V2StaticLegislationBriefingProps = {
  law: StaticLegislationBriefingLaw
  applies: boolean
}

export function V2StaticLegislationBriefing({
  law,
  applies,
}: V2StaticLegislationBriefingProps) {
  const { t } = useTranslation('common')
  const base = `v2.pages.products.detail.${law}Briefing`

  if (!applies) {
    return null
  }

  return (
    <div className='space-y-6'>
      {LAWS_WITH_OVERVIEW.has(law) && (
        <Alert variant='info'>
          <AlertTitle className='line-clamp-none'>
            {t(`${base}.overview.title`)}
          </AlertTitle>
          <AlertDescription className='space-y-2'>
            <p>{t(`${base}.overview.body`)}</p>
          </AlertDescription>
        </Alert>
      )}

      <div className='space-y-4'>
        <div className='flex flex-col items-center gap-2 rounded-lg border bg-background px-4 py-3 text-center shadow-sm'>
          <StaticLegislationComingSoonBadge withTooltip={false} />
          <p className='max-w-sm text-sm text-muted-foreground'>
            {t(`${COMING_SOON_BASE}.body`)}
          </p>
        </div>
        <section aria-hidden className='pointer-events-none select-none space-y-3'>
          <div className='space-y-1 blur-lg'>
            <div className='h-5 w-36 rounded bg-foreground/20' />
            <div className='h-3 w-2/3 rounded bg-foreground/15' />
          </div>
          <ol className='divide-y rounded-md border'>
            {FAKE_STEP_BARS.map((bar, stepIndex) => (
              <li key={`${bar.title}-${stepIndex}`} className='px-3 py-3'>
                <div className='flex items-start gap-3'>
                  {bar.done ? (
                    <span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'>
                      <Check className='size-3' />
                    </span>
                  ) : (
                    <span className='flex size-5 shrink-0 items-center justify-center text-xs text-muted-foreground tabular-nums'>
                      {stepIndex + 1}
                    </span>
                  )}
                  <div className='flex min-w-0 flex-1 items-start justify-between gap-3'>
                    <div className='min-w-0 flex-1 space-y-2 blur-lg'>
                      <div className='flex flex-wrap items-center gap-1.5'>
                        <div
                          className={`h-3.5 rounded bg-foreground/25 ${bar.title}`}
                        />
                        <div className='h-5 w-16 rounded-md border' />
                      </div>
                      <div
                        className={`h-2.5 rounded bg-foreground/15 ${bar.body}`}
                      />
                    </div>
                    {bar.action && (
                      <div className='h-8 w-20 shrink-0 rounded-md border bg-muted blur-md' />
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  )
}
